import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useAppSelector } from '@hooks/redux';
import type { Schedule } from '@modules/schedules/types/objects';

// Same predicate the schedules reducer used when an upload arrived. The week/function
// comparison is strict, so a new upload is appended and older rows stay in the list.
function shouldKeepSchedule(schedule: Schedule, uploaded: Schedule) {
  return !(schedule.function_id === (uploaded.week as unknown as Schedule['function_id'])
    && schedule.week === uploaded.week);
}

export function appendUploadedSchedule(schedules: Schedule[], uploaded: Schedule) {
  return [
    ...schedules.filter((schedule) => shouldKeepSchedule(schedule, uploaded)),
    uploaded,
  ];
}

interface UploadedSchedulesContextValue {
  schedules: Schedule[];
  addSchedule: (schedule: Schedule) => void;
}

const UploadedSchedulesContext = createContext<UploadedSchedulesContextValue | null>(null);

export function UploadedSchedulesProvider({ children }: { children: React.ReactNode }) {
  const networkId = useAppSelector((state) => state.network.selected);
  const previousNetworkId = useRef(networkId);
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  useEffect(() => {
    if (previousNetworkId.current === networkId) return;
    previousNetworkId.current = networkId;
    setSchedules([]);
  }, [networkId]);

  const addSchedule = useCallback((schedule: Schedule) => {
    setSchedules((current) => appendUploadedSchedule(current, schedule));
  }, []);

  const value = useMemo(() => ({ schedules, addSchedule }), [schedules, addSchedule]);

  return (
    <UploadedSchedulesContext.Provider value={value}>
      {children}
    </UploadedSchedulesContext.Provider>
  );
}

export function useUploadedSchedules() {
  const context = useContext(UploadedSchedulesContext);
  if (!context) {
    throw new Error('useUploadedSchedules must be used within UploadedSchedulesProvider');
  }
  return context;
}
